import { Injectable } from '@angular/core';
import { ConnectionService } from '@services/connection-service';
import { EventLoggerService } from '@services/event-logger-service';
import { InstantCheckupService } from '@services/instant-checkup-service';
import { FallbackSummaryMap, FallbackSummaries } from 'e2e/src/shared/constants';
import { problemsCopy } from '../../app/instant-checkup/problem-details';
import { AppConfig } from '../../app/app.config';
import { extractRegimenProducts, extractHeroIngredients } from 'client/helper/regimen-helper';
import { ApiClientConstant } from '@cureskin/api-client';
import {
  InstantCheckup, RegimenVariant, HeroIngredient, User,
} from '@cureskin/api-client/src/server-api';
import {
  ConcernsWithCount, SkinData, HairData, SkinParameters, ConcernCountResult, HeroIngredientsResult,
  PRIVATE_MAIN_CONCERN_DISPLAY_NAME, HAIR_CONCERNS, CONCERN_NAMES,
  PROBLEM_TO_CONCERN, FIRST_JOURNEY_CONCERNS, HAIR_REPORT_FALLBACK_PARAMS, DetectionResult, SkinReportInfo,
} from '../../app/report/report.constants';
import { Regimen } from "../../app/app-constant-types";


@Injectable()
export class ReportService {
  static readonly MINIMUM_REQUIRED_HERO_INGREDIENTS = 3;
  static readonly MAXIMUM_REQUIRED_HERO_INGREDIENTS = 6;
  static readonly MINIMUM_REQUIRED_HERO_INGREDIENTS_EXISTING_UI = 4;

  constructor(
    private readonly conn: ConnectionService,
    private readonly appConfig: AppConfig,
    private readonly instantCheckupService: InstantCheckupService,
    private readonly eventLogger: EventLoggerService,
  ) {}

  filterCheckups(checkups: InstantCheckup[], isHair: boolean): InstantCheckup[] {
    if (!checkups?.length) return [];

    const filtered: InstantCheckup[] = [];
    if (isHair) {
      const hairFront = checkups?.find((c: InstantCheckup): boolean => c?.type?.includes(ApiClientConstant.InstantCheckup.Type.HAIR_FRONT));
      const hairTop = checkups?.find((c: InstantCheckup): boolean => c?.type?.includes(ApiClientConstant.InstantCheckup.Type.HAIR_TOP));
      const hairLegacy = checkups?.find((c: InstantCheckup): boolean => c?.type === ApiClientConstant.InstantCheckup.Type.HAIR);

      if (hairFront) filtered.push(hairFront);
      else if (hairLegacy) filtered.push(hairLegacy);
      if (hairTop) filtered.push(hairTop);
    } else {
      const frontFace = checkups?.find((c: InstantCheckup): boolean => c?.type?.includes(ApiClientConstant.InstantCheckup.Type.FRONT_FACE));
      const sideFace = checkups?.find((c: InstantCheckup): boolean => c?.type?.includes(ApiClientConstant.InstantCheckup.Type.SIDE_FACE));

      if (frontFace) filtered.push(frontFace);
      if (sideFace) filtered.push(sideFace);
    }
    return filtered;
  }

  mergeDetectedConcerns(checkups: InstantCheckup[]): { mergedMap: Map<string, DetectionResult>, mergedConcerns: DetectionResult[] } {
    const mergedMap = new Map<string, DetectionResult>();
    const mergedConcerns: DetectionResult[] = [];

    if (!checkups?.length) return { mergedMap, mergedConcerns };

    const frontFaceDetection = checkups.find(
      (item: InstantCheckup): boolean => item.type === this.appConfig.Shared.InstantCheckup.Type.FRONT_FACE);
    const sideFaceDetection = checkups.find(
      (item: InstantCheckup): boolean => item.type !== this.appConfig.Shared.InstantCheckup.Type.FRONT_FACE);

    const allObjects: DetectionResult[] = [
      ...(frontFaceDetection?.aiResponse?.result || []),
      ...(sideFaceDetection?.aiResponse?.result || []),
    ];

    allObjects.forEach((checkup: DetectionResult): void => {
      const name = checkup?.ProblemName || problemsCopy.en[checkup.class]?.name;
      const existing = mergedMap.get(name);
      if (!existing || (checkup.BoundingBoxes?.length || 0) > (existing.BoundingBoxes?.length || 0)) {
        mergedMap.set(name, checkup);
      }
    });

    mergedMap.forEach((item: DetectionResult): void => {
      mergedConcerns.push(item);
    });

    return { mergedMap, mergedConcerns };
  }

  buildConcernCounts(mergedMap: Map<string, DetectionResult>, regimenConcernName: string): ConcernCountResult {
    const payload: ConcernsWithCount = {
      infAcne: 0, comedone: 0, openPores: 0, ds: 0, dc: 0, melasma: 0, acneScars: 0, sensitiveSkin: 0,
    };
    let mainConcernKey = 'infAcne';
    let mainConcern = '';

    mergedMap.forEach((obj: DetectionResult): void => {
      const mapping = PROBLEM_TO_CONCERN[obj?.ProblemName];
      if (!mapping) return;
      mainConcernKey = mapping.key;
      if (obj?.Condition === 'Detected') {
        payload[mapping.key] = mapping.useBoundingBoxCount ? (obj?.BoundingBoxes?.length ?? 0) : 1;
        if (!regimenConcernName && !mainConcern) {
          mainConcern = CONCERN_NAMES[mapping.key] || '';
        }
      }
    });

    return { payload, mainConcernKey, mainConcern };
  }

  parseSkinReportParams(regimen: Regimen, skinReport: SkinReportInfo, showBasedOnRegimen: boolean): SkinData {
    const skinReportParamsFromRegimen = regimen?.skinReportParams || {};
    const { skinParameters } = skinReportParamsFromRegimen;
    const skinReportFallback = skinReport?.skinParameters;

    const parsed = this.parseStringifiedParams<SkinData>(skinParameters);

    if (showBasedOnRegimen) {
      return Object.keys(parsed).length > 0 ? parsed : skinReportFallback;
    }
    return skinReportFallback;
  }

  parseHairReportParams(regimen: Regimen): HairData {
    const hairParameters = regimen?.hairReportParams?.hairParameters;
    if (hairParameters) {
      return this.parseStringifiedParams<HairData>(hairParameters);
    }
    return this.getHairReportFallbackParams(regimen);
  }

  private getHairReportFallbackParams(regimen: Regimen): HairData {
    const concernKey: string = regimen?.displayConcern?.treatmentConcerns?.[0] ?? '';
    const concernFallback = HAIR_REPORT_FALLBACK_PARAMS[concernKey];
    if (!concernFallback) return {} as HairData;

    if (concernKey === 'HAIR_LOSS') {
      const rawSeverity: string = regimen?.displayConcern?.severity || '';
      const severity = rawSeverity
        ? rawSeverity.charAt(0).toUpperCase() + rawSeverity.slice(1).toLowerCase()
        : 'Moderate';
      return (concernFallback[severity] ?? concernFallback['Moderate'] ?? {}) as HairData;
    }

    return (concernFallback['Moderate'] ?? {}) as HairData;
  }

  private parseStringifiedParams<T extends SkinData | HairData>(params: Record<string, string | SkinParameters>): T {
    const parsed: Record<string, string | SkinParameters> = { ...params };
    Object.keys(parsed).forEach((key: string): void => {
      if (typeof parsed[key] === 'string') {
        if ((parsed[key] as string).trim() === '') {
          parsed[key] = { patientScore: '', expectedRange: '' } as unknown as SkinParameters;
        } else {
          try {
            const formattedString = (parsed[key] as string)
              .replace(/\b(\w+)\b:/g, '"$1":')
              .replace(/\b(\d{1,4})-(\d{1,4})\b/g, '"$1-$2"');
            parsed[key] = JSON.parse(formattedString);
          } catch {
            parsed[key] = { patientScore: '', expectedRange: '' } as unknown as SkinParameters;
          }
        }
      }
    });
    return parsed as unknown as T;
  }

  async resolveOverallSummary(
    regimen: Regimen, skinReport: SkinReportInfo, isHair: boolean, mainConcern: string, concernsWithCount: ConcernsWithCount,
    userLanguage: string = ApiClientConstant.LanguageString.EN,
  ): Promise<string> {
    if (isHair) {
      const hairSummary = await this.fetchHairOverallSummary(regimen);
      return hairSummary || this.assignFallbackSummary(mainConcern, userLanguage, true);
    }

    const regimenSummary = regimen?.skinReportParams?.overallSummary;
    if (regimenSummary) return regimenSummary;

    const fallbackSummary = skinReport?.overallSummary?.overallSummary;
    if (fallbackSummary) return fallbackSummary;

    this.logSkinReportParams(concernsWithCount);
    return this.assignFallbackSummary(mainConcern, userLanguage);
  }

  private async fetchHairOverallSummary(regimen: Regimen): Promise<string> {
    const pointer = regimen?.overallSummary;
    if (pointer?.objectId) {
      const [languageString] = await this.conn.getLanguageStringsById([pointer.objectId]);
      if (languageString) {
        return this.conn.getValueOfLanguageString(languageString);
      }
    }
    return '';
  }

  private assignFallbackSummary(
    mainConcern: string,
    userLanguage: string = ApiClientConstant.LanguageString.EN,
    isHair: boolean = false,
  ): string {
    const lang = FallbackSummaryMap[userLanguage] ? userLanguage : ApiClientConstant.LanguageString.EN;
    const langMap = FallbackSummaryMap[lang];
    const langSummaries = FallbackSummaries[lang] || FallbackSummaries.en;

    const matchingKey = Object.keys(langMap).find(
      (key: string): boolean => key === mainConcern
    );

    const defaultSummary = isHair ? langSummaries.GENERAL_HAIR : langSummaries.GENERAL;
    return matchingKey ? langMap[matchingKey] : defaultSummary;
  }

  resolvePrimaryConcern(regimen: Regimen, mainConcern: string, detectedConcerns: string[], isHair: boolean, userLanguage: string = ApiClientConstant.LanguageString.EN): string {
    if (isHair) {
      const treatmentConcerns: string[] = regimen?.displayConcern?.treatmentConcerns ?? [];
      const lang = PRIVATE_MAIN_CONCERN_DISPLAY_NAME[userLanguage] ? userLanguage : ApiClientConstant.LanguageString.EN;
      return PRIVATE_MAIN_CONCERN_DISPLAY_NAME[lang]?.[treatmentConcerns[0]] ?? PRIVATE_MAIN_CONCERN_DISPLAY_NAME[lang]['REGULAR_HAIR_CARE'] ?? '';
    }
    return regimen?.displayConcern?.name || mainConcern || this.getFirstValidDetectedConcern(detectedConcerns);
  }

  getFirstValidDetectedConcern(detectedConcerns: string[]): string {
    return detectedConcerns?.find(
      (concern: string) => !HAIR_CONCERNS.some(hc => concern.toLowerCase().includes(hc))
    ) ?? '';
  }

  resolveOtherConcerns(
    regimen: Regimen, mainConcern: string, detectedConcerns: string[],
    firstFrontFaceCheckup: InstantCheckup | undefined, isHair: boolean, userLanguage: string = ApiClientConstant.LanguageString.EN,
  ): string {
    if (isHair) return this.resolveHairOtherConcerns(regimen, detectedConcerns, userLanguage);

    const concernString = this.getConcernName(mainConcern);
    const melasmaSeverity = this.getMelasmaSeverity(firstFrontFaceCheckup);
    const melasmaConcern = this.appConfig.Shared.SkinReport.Concern.MELASMA;
    const isMildMelasma = melasmaSeverity === this.appConfig.Shared.InstantCheckup.Severity.MILD;
    const treatmentConcerns: string[] = regimen?.displayConcern?.treatmentConcerns ?? [];
    const lang = PRIVATE_MAIN_CONCERN_DISPLAY_NAME[userLanguage] ? userLanguage : ApiClientConstant.LanguageString.EN;

    const treatmentConcernDisplayNames = treatmentConcerns
      .map((tc: string): string => PRIVATE_MAIN_CONCERN_DISPLAY_NAME[lang]?.[tc] ?? tc)
      .filter((name: string): boolean => !!name);

    return detectedConcerns
      .filter((concern: string): boolean => {
        const isMainConcern = concern.toLowerCase().trim() === concernString.toLowerCase().trim();
        const isMelasmaButNotMild = concern === melasmaConcern && !isMildMelasma;
        const isHairConcern = HAIR_CONCERNS.some(hc => concern.toLowerCase().includes(hc));
        const isInTreatmentConcerns = treatmentConcernDisplayNames.some(
          (displayName: string): boolean => displayName.toLowerCase().trim() === concern.toLowerCase().trim()
        );
        return !isMainConcern && (concern !== melasmaConcern || isMelasmaButNotMild) && !isHairConcern && !isInTreatmentConcerns;
      }).join(', ');
  }

  private resolveHairOtherConcerns(regimen: Regimen, detectedConcerns: string[], userLanguage: string = ApiClientConstant.LanguageString.EN): string {
    const treatmentConcerns: string[] = regimen?.displayConcern?.treatmentConcerns ?? [];
    const secondaryConcerns: string[] = [];
    const lang = PRIVATE_MAIN_CONCERN_DISPLAY_NAME[userLanguage] ? userLanguage : ApiClientConstant.LanguageString.EN;

    if (treatmentConcerns[1]) {
      const displayName = PRIVATE_MAIN_CONCERN_DISPLAY_NAME[lang]?.[treatmentConcerns[1]] ?? treatmentConcerns[1];
      secondaryConcerns.push(displayName);
    }

    const hasTempleBaldness = detectedConcerns?.some(
      (concern: string): boolean => concern.toLowerCase().includes('temple-baldness')
    );
    if (hasTempleBaldness) {
      secondaryConcerns.push('Temple baldness');
    }

    return secondaryConcerns.join(', ');
  }

  resolveSeverity(regimen: Regimen, firstFrontFaceCheckup: InstantCheckup | undefined, mainConcern: string): string {
    const severityFromRegimen = regimen?.displayConcern?.severity;
    if (severityFromRegimen) return this.capitalizeFirstLetter(severityFromRegimen);

    const concernString = this.getConcernName(mainConcern);
    const checkupResults: DetectionResult[] = firstFrontFaceCheckup?.aiResponse?.result || [];
    const mainConcernDetections = checkupResults.find((r: DetectionResult): boolean => r?.ProblemName === concernString);

    const isDetected = mainConcernDetections?.Condition === this.appConfig.Shared.InstantCheckup.DetectionStatus.DETECTED;
    if (isDetected) {
      return this.instantCheckupService.getSeverityFromConcern(mainConcernDetections) ?? this.appConfig.Shared.SkinReport.Severity.MODERATE;
    }
    return this.appConfig.Shared.SkinReport.Severity.MODERATE;
  }

  getFirstFrontFaceCheckup(checkups: InstantCheckup[], isHair: boolean): InstantCheckup | undefined {
    if (!checkups?.length) return undefined;
    const targetType = isHair
      ? this.appConfig.Shared.InstantCheckup.Type.HAIR_FRONT
      : this.appConfig.Shared.InstantCheckup.Type.FRONT_FACE;
    return checkups.find((c: InstantCheckup): boolean => c.type === targetType);
  }

  async getOrCreateReportInfo(user: User): Promise<{ reportCode: string, patientID: string, createdAt: Date }> {
    const existingReport = user?.get('report') as { reportCode: string, patientID: string, createdAt: Date } | undefined;
    if (existingReport) {
      return existingReport;
    }

    const reportCode = `CS${this.generateRandomNumberWithDigits(7)}`;
    const patientID = `CS${this.generateRandomNumberWithDigits(4)}`;
    const createdAt = new Date();

    user?.set('report', { reportCode, patientID, createdAt });
    await user.save();

    return { reportCode, patientID, createdAt };
  }

  resolveHeroIngredients(regimen: Regimen, isRebranded: boolean, count: number, isHairReport: boolean = false): HeroIngredientsResult {
    if (!regimen?.variants) return { ingredients: [], show: false };

    const variantWithFiveProducts = regimen.variants.find((v: RegimenVariant): boolean => v?.variantId === 'variant_1999');
    const regimenProducts = extractRegimenProducts(variantWithFiveProducts);
    let ingredients: HeroIngredient[] = extractHeroIngredients(regimenProducts) || [];

    let show: boolean;
    if (isHairReport) {
      ingredients = ingredients.slice(0, ReportService.MAXIMUM_REQUIRED_HERO_INGREDIENTS);
      show = ingredients.length >= ReportService.MINIMUM_REQUIRED_HERO_INGREDIENTS;
    } else if (isRebranded && ingredients.length > 0) {
      ingredients = ingredients.slice(0, count);
      show = ingredients.length >= ReportService.MINIMUM_REQUIRED_HERO_INGREDIENTS;
    } else {
      ingredients = ingredients.slice(0, ReportService.MAXIMUM_REQUIRED_HERO_INGREDIENTS);
      show = ingredients.length === ReportService.MINIMUM_REQUIRED_HERO_INGREDIENTS
        || ingredients.length > ReportService.MINIMUM_REQUIRED_HERO_INGREDIENTS_EXISTING_UI;
    }

    return { ingredients, show };
  }

  shouldShowFirstJourney(mainConcern: string): boolean {
    return FIRST_JOURNEY_CONCERNS.includes(mainConcern);
  }

  getConcernName(concern: string): string {
    if (concern === CONCERN_NAMES.infAcne) {
      return this.appConfig.Shared.SkinReport.Concern.INFLAMMATORY_ACNE;
    }
    return concern;
  }

  capitalizeFirstLetter(str: string): string {
    if (!str) return '';
    return str.split(' ').map((item: string): string => item[0] + item.slice(1).toLowerCase()).join(' ');
  }

  private getMelasmaSeverity(firstFrontFaceCheckup: InstantCheckup | undefined): string {
    const results: DetectionResult[] = firstFrontFaceCheckup?.aiResponse?.result || [];
    const melasmaDetails = results.find(
      (d: DetectionResult): boolean => d.ProblemName === this.appConfig.Shared.SkinReport.Concern.MELASMA
        && d.Condition === this.appConfig.Shared.InstantCheckup.DetectionStatus.DETECTED);
    return melasmaDetails?.Severity;
  }

  private generateRandomNumberWithDigits(digits: number): string {
    const multiplier = 10 ** (digits - 1);
    return (Math.floor(Math.random() * 9 * multiplier) + multiplier).toString();
  }

  logSkinReportParams(concernsWithCount: ConcernsWithCount): void {
    this.eventLogger.trackInElasticSearch({
      event: 'SKIN_REPORT_PARAM_EMPTY',
      type: 'ERROR_SKIN_REPORT_PARAM_EMPTY',
      data: JSON.stringify(concernsWithCount),
    });
  }
}
